/**
 * Activation Keys Management
 * - Generate new keys (with optional note)
 * - List keys with assigned playlists count and device info
 * - Assign multiple playlists to a key
 * - Delete keys
 */
import React, { useEffect, useState } from "react";
import {
  listKeys,
  generateKey,
  deleteKey,
  listPlaylists,
  createPlaylist,
  assignPlaylistsToKey,
  getKeyDetails,
  updateKeyStatus,
  convertTrialToPaid,
  renewKey,
  setKeyDebugMode,
  getSettings,
  connectMacToKey,
  disconnectMacFromKey,
} from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Copy, Trash2, ListPlus, Plus, KeyRound, PlusCircle, X, FlaskConical, ArrowUpCircle, RefreshCcw, Search, Link2, Link2Off } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ADMIN } from "../lib/testIds";

const statusColors = {
  unused: "bg-slate-100 text-slate-700",
  active: "bg-emerald-100 text-emerald-700",
  expired: "bg-amber-100 text-amber-700",
  disabled: "bg-red-100 text-red-700",
};

export default function Keys() {
  const { toast } = useToast();
  const [keys, setKeys] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generate dialog
  const [genOpen, setGenOpen] = useState(false);
  const [genNote, setGenNote] = useState("");
  const [genMac, setGenMac] = useState("");
  const [genIsTrial, setGenIsTrial] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [keyPolicy, setKeyPolicy] = useState({
    key_validity_days: 0,
    key_credit_cost: 1,
    trial_validity_days: 5,
    trial_credit_cost: 0,
  });

  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [activeKey, setActiveKey] = useState(null);
  const [selectedPlaylistIds, setSelectedPlaylistIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Inline create-playlist form (inside assign dialog)
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [inlineForm, setInlineForm] = useState({ name: "", m3u_url: "", description: "" });
  const [creatingInline, setCreatingInline] = useState(false);

  // v1.4.38 — Connect MAC field in the assign-key dialog. `macDraft`
  // is the input value; `boundMac` is the currently-saved MAC on the
  // key (mirrors `activeKey.bound_mac_address`). `macBusy` shows a
  // spinner while the connect/disconnect request is in flight.
  const [macDraft, setMacDraft] = useState("");
  const [boundMac, setBoundMac] = useState(null);
  const [macBusy, setMacBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [k, p, s] = await Promise.all([listKeys(), listPlaylists(), getSettings()]);
      setKeys(k);
      setPlaylists(p);
      setKeyPolicy({
        key_validity_days: s.key_validity_days ?? 0,
        key_credit_cost: s.key_credit_cost ?? 1,
        trial_validity_days: s.trial_validity_days ?? 5,
        trial_credit_cost: s.trial_credit_cost ?? 0,
      });
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const newKey = await generateKey(genNote || null, { is_trial: genIsTrial });
      // v2.2.1-α13 — Optional MAC binding at generation time. If the
      // admin filled in the MAC field we immediately call the same
      // connect-mac endpoint the assign dialog uses, so the freshly
      // minted key is already bound before the toast even appears.
      const macTrim = genMac.trim();
      if (macTrim) {
        try {
          await connectMacToKey(newKey.id, macTrim);
        } catch (macErr) {
          toast({
            title: "Key created, but MAC bind failed",
            description: macErr.response?.data?.detail || macErr.message,
            variant: "destructive",
          });
        }
      }
      toast({
        title: genIsTrial ? "Test activation key generated" : "Activation key generated",
        description: `Key: ${newKey.key}${macTrim ? ` · MAC ${macTrim}` : ""}`,
      });
      setGenOpen(false);
      setGenNote("");
      setGenMac("");
      setGenIsTrial(false);
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const openGenerateDialog = (trial) => {
    setGenIsTrial(trial);
    setGenNote("");
    setGenOpen(true);
  };

  const handleDelete = async (key) => {
    const label = key.key;
    const ownerHint =
      key.device ? `\nThis key is currently activated on a device — that device will be locked out.` : "";
    if (
      !window.confirm(
        `Delete activation key ${label}?\n\n` +
          `• All playlist assignments will be removed.${ownerHint}\n` +
          `• This cannot be undone.`
      )
    )
      return;
    try {
      await deleteKey(key.id);
      toast({ title: "Key deleted", description: label });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard", description: text });
  };

  const openAssignDialog = async (key) => {
    setActiveKey(key);
    setShowInlineForm(false);
    setInlineForm({ name: "", m3u_url: "", description: "" });
    setAssignSearch("");
    setMacDraft("");
    setBoundMac(key.bound_mac_address || null);
    try {
      const details = await getKeyDetails(key.id);
      setSelectedPlaylistIds((details.playlists || []).map((p) => p.id));
      // Prefer freshly-fetched detail — the row might be stale.
      setBoundMac(details.key?.bound_mac_address || null);
      setAssignOpen(true);
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // v1.4.38 — Connect/disconnect MAC handlers for the dialog.
  const handleConnectMac = async () => {
    if (!activeKey || !macDraft.trim()) {
      toast({ title: "Enter a MAC address", variant: "destructive" });
      return;
    }
    setMacBusy(true);
    try {
      await connectMacToKey(activeKey.id, macDraft.trim());
      const cleaned = macDraft.trim().replace(/[^0-9a-fA-F]/g, "").toUpperCase();
      setBoundMac(cleaned);
      setMacDraft("");
      toast({
        title: "MAC connected",
        description: `Client at ${cleaned} is now bound to this key.`,
      });
      refresh();
    } catch (e) {
      toast({
        title: "Cannot connect MAC",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setMacBusy(false);
    }
  };

  const handleDisconnectMac = async () => {
    if (!activeKey) return;
    setMacBusy(true);
    try {
      await disconnectMacFromKey(activeKey.id);
      setBoundMac(null);
      toast({ title: "MAC disconnected from this key" });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setMacBusy(false);
    }
  };

  const togglePlaylist = (id) => {
    setSelectedPlaylistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleInlineCreate = async () => {
    if (!inlineForm.name || !inlineForm.m3u_url) {
      toast({ title: "Name and M3U URL required", variant: "destructive" });
      return;
    }
    setCreatingInline(true);
    try {
      const created = await createPlaylist(inlineForm);
      toast({
        title: "Playlist created",
        description: `${created.channels_count} channels parsed and auto-selected`,
      });
      // Refresh playlist list and auto-select the newly created one
      const updated = await listPlaylists();
      setPlaylists(updated);
      setSelectedPlaylistIds((prev) => [...prev, created.id]);
      setShowInlineForm(false);
      setInlineForm({ name: "", m3u_url: "", description: "" });
    } catch (e) {
      toast({
        title: "Error creating playlist",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setCreatingInline(false);
    }
  };

  const saveAssignments = async () => {
    setAssigning(true);
    try {
      await assignPlaylistsToKey(activeKey.id, selectedPlaylistIds);
      toast({
        title: "Playlists assigned",
        description: `${selectedPlaylistIds.length} playlists linked to key`,
      });
      setAssignOpen(false);
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleStatus = async (key) => {
    const newStatus = key.status === "disabled" ? "active" : "disabled";
    try {
      await updateKeyStatus(key.id, newStatus);
      toast({ title: `Key ${newStatus}` });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleConvertTrial = async (key) => {
    const validityLabel =
      keyPolicy.key_validity_days > 0
        ? `${keyPolicy.key_validity_days} days`
        : "lifetime (no expiry)";
    const ok = window.confirm(
      `Convert trial key ${key.key} to a paid key?\n\n` +
        `• Validity will become: ${validityLabel}\n` +
        `• Cost: ${keyPolicy.key_credit_cost} credit${keyPolicy.key_credit_cost === 1 ? "" : "s"} (resellers only)\n\n` +
        `This action cannot be undone.`
    );
    if (!ok) return;
    try {
      const updated = await convertTrialToPaid(key.id);
      toast({
        title: "Trial converted to paid",
        description: `Key ${updated.key} is now a paid key.`,
      });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  // v1.3.41 — optimistic toggle of the per-key Debug-mode flag.
  // Server is the source of truth; on failure we revert the row.
  const handleToggleKeyDebug = async (key, next) => {
    const apply = (v) => setKeys((prev) =>
      prev.map((k) => k.id === key.id ? { ...k, debug_mode_enabled: !!v } : k)
    );
    apply(next);
    try {
      await setKeyDebugMode(key.id, !!next);
      toast({
        title: next ? "Debug mode enabled" : "Debug mode disabled",
        description: `Device using ${key.key} will pick up the change on next refresh.`,
      });
    } catch (e) {
      apply(!next);
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  // v1.3.37 — Renew an activation key: extends its expiry by the
  // configured global `key_validity_days`. Disabled / hidden for
  // lifetime keys (expires_at is null) — they don't need renewal.
  const handleRenewKey = async (key) => {
    const days = keyPolicy.key_validity_days;
    const ok = window.confirm(
      `Renew activation key ${key.key}?\n\n`
      + `• Adds ${days} more day${days === 1 ? "" : "s"} on top of the current expiry.\n`
      + `• If the key already expired, the ${days}-day window starts from today.\n`
      + `• Expired keys are automatically re-activated.`
    );
    if (!ok) return;
    try {
      const updated = await renewKey(key.id);
      toast({
        title: "Key renewed",
        description: `${updated.key} now expires ${new Date(updated.expires_at).toLocaleDateString()}.`,
      });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-slate-600">
          Generate activation keys and assign playlists to control player access
        </p>
        <div className="flex items-center gap-2">
          <Button
            data-testid="btn-generate-test-key"
            onClick={() => openGenerateDialog(true)}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <FlaskConical className="w-4 h-4 mr-2" />
            Generate Test Key ({keyPolicy.trial_validity_days}d)
          </Button>
          <Button
            data-testid={ADMIN.btnGenerateKey}
            onClick={() => openGenerateDialog(false)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate Key
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : keys.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <KeyRound className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No activation keys yet. Generate one to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Playlists</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-center">Debug</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id} data-testid={ADMIN.keyRow(key.id)}>
                    <TableCell className="font-mono font-semibold">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{key.key}</span>
                        <button
                          data-testid={ADMIN.btnCopyKey(key.id)}
                          onClick={() => handleCopy(key.key)}
                          className="text-slate-400 hover:text-slate-900"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        {key.is_trial && (
                          <Badge
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px] font-semibold uppercase tracking-wide"
                          >
                            <FlaskConical className="w-3 h-3 mr-0.5" />
                            Trial
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* v1.4.66 — Owner badge (admin / reseller / client) */}
                      {key.owner_username ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="text-xs font-medium text-slate-800">
                            {key.owner_username}
                          </span>
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                              key.owner_role === "admin"
                                ? "bg-purple-100 text-purple-700"
                                : key.owner_role === "reseller"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                            data-testid={`key-owner-role-${key.id}`}
                          >
                            {key.owner_role || "client"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleStatus(key)}
                        className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          statusColors[key.status] || statusColors.unused
                        }`}
                      >
                        {key.status}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {key.assigned_playlists_count} assigned
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {key.device ? (
                        <div>
                          <div className="font-medium text-slate-900">
                            {key.device.device_model || key.device.platform}
                          </div>
                          <div className="text-xs text-slate-500 font-mono">
                            {key.device.device_id}
                          </div>
                        </div>
                      ) : key.bound_mac_address ? (
                        <div className="text-xs" data-testid={`key-bound-mac-${key.id}`}>
                          <div className="inline-flex items-center gap-1 text-orange-700 font-medium">
                            <Link2 className="w-3 h-3" />
                            Bound MAC
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            {key.bound_mac_address.replace(/(.{2})/g, "$1:").replace(/:$/, "")}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400">Not activated</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                      {key.note || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      <div>{new Date(key.created_at).toLocaleString()}</div>
                      {key.expires_at && (
                        <div className="text-[11px] text-amber-700 mt-0.5">
                          Expires: {new Date(key.expires_at).toLocaleDateString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        data-testid={`admin-key-debug-${key.id}`}
                        checked={!!key.debug_mode_enabled}
                        onCheckedChange={(v) => handleToggleKeyDebug(key, v)}
                        title="Force debug mode on the device that uses this key"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {key.is_trial && (
                          <Button
                            data-testid={`btn-convert-trial-${key.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleConvertTrial(key)}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            title="Convert this trial key to a paid key"
                          >
                            <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />
                            Convert
                          </Button>
                        )}
                        {/* v1.3.37 — Renew button. Hidden for lifetime
                            keys (expires_at = null) because they have
                            no expiry to extend. */}
                        {key.expires_at && (
                          <Button
                            data-testid={`btn-renew-key-${key.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleRenewKey(key)}
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                            title={`Extend expiry by ${keyPolicy.key_validity_days} day(s)`}
                            disabled={!keyPolicy.key_validity_days}
                          >
                            <RefreshCcw className="w-3.5 h-3.5 mr-1" />
                            Renew
                          </Button>
                        )}
                        <Button
                          data-testid={ADMIN.btnAssignPlaylists(key.id)}
                          size="sm"
                          variant="outline"
                          onClick={() => openAssignDialog(key)}
                          title={
                            key.assigned_playlists_count > 0
                              ? "Re-assign / edit playlists for this key"
                              : "Assign playlists to this key"
                          }
                        >
                          <ListPlus className="w-3.5 h-3.5 mr-1" />
                          {key.assigned_playlists_count > 0 ? "Edit Playlists" : "Assign"}
                        </Button>
                        <Button
                          data-testid={ADMIN.btnDeleteKey(key.id)}
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(key)}
                          className="border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                          title="Permanently delete this activation key"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid={ADMIN.generateKeyDialog}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {genIsTrial ? (
                <>
                  <FlaskConical className="w-4 h-4 text-emerald-600" />
                  Generate Test / Trial Activation Key
                </>
              ) : (
                "Generate New Activation Key"
              )}
            </DialogTitle>
            <DialogDescription>
              {genIsTrial
                ? "A short-lived key your client can use to evaluate the player. Expires automatically."
                : "A unique 12-character key will be created. Share it with the customer to activate their player."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {genIsTrial ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900 space-y-0.5">
                <div>
                  <span className="font-semibold">Validity:</span>{" "}
                  {keyPolicy.trial_validity_days} days from activation
                </div>
                <div>
                  <span className="font-semibold">Cost:</span>{" "}
                  {keyPolicy.trial_credit_cost === 0
                    ? "Free (no credits deducted)"
                    : `${keyPolicy.trial_credit_cost} credit${keyPolicy.trial_credit_cost === 1 ? "" : "s"}`}
                </div>
                <div className="text-[11px] text-emerald-700 mt-1">
                  Configure in <span className="font-semibold">Settings → Activation Key Policy</span>.
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-orange-200 bg-orange-50/60 px-3 py-2 text-xs text-orange-900 space-y-0.5">
                <div>
                  <span className="font-semibold">Validity:</span>{" "}
                  {keyPolicy.key_validity_days > 0
                    ? `${keyPolicy.key_validity_days} days from activation`
                    : "Lifetime (no expiry)"}
                </div>
                <div>
                  <span className="font-semibold">Cost:</span>{" "}
                  {keyPolicy.key_credit_cost} credit
                  {keyPolicy.key_credit_cost === 1 ? "" : "s"} per key (resellers only)
                </div>
                <div className="text-[11px] text-orange-700 mt-1">
                  Change defaults in <span className="font-semibold">Settings → Activation Key Policy</span>.
                </div>
              </div>
            )}
            <Label htmlFor="note">Note (optional)</Label>
            <Input
              id="note"
              data-testid={ADMIN.generateKeyNote}
              placeholder={genIsTrial ? "e.g. Trial for John — review meeting Fri" : "e.g. Customer John, paid annual"}
              value={genNote}
              onChange={(e) => setGenNote(e.target.value)}
            />
            <Label htmlFor="gen-mac">Pre-bind MAC address (optional)</Label>
            <Input
              id="gen-mac"
              data-testid="generate-key-mac"
              placeholder="AA:BB:CC:DD:EE:FF"
              value={genMac}
              onChange={(e) => setGenMac(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground -mt-2">
              When set, the native Android player auto-fills this key on first launch for the matching device MAC.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={ADMIN.generateKeyConfirm}
              onClick={handleGenerate}
              disabled={generating}
              className={
                genIsTrial
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-orange-500 hover:bg-orange-600"
              }
            >
              {generating ? "Generating..." : genIsTrial ? "Generate Test Key" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Playlists Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid={ADMIN.assignDialog} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {(activeKey?.assigned_playlists_count || 0) > 0
                ? "Edit / Re-assign Playlists"
                : "Assign Playlists"}
            </DialogTitle>
            <DialogDescription>
              {(activeKey?.assigned_playlists_count || 0) > 0
                ? "Tick or untick playlists to update what this key can access. Saving replaces the current assignments."
                : "Select playlists to assign to this key. One key can have multiple playlists."}{" "}
              Key: <span className="font-mono font-semibold">{activeKey?.key}</span>.
            </DialogDescription>
          </DialogHeader>

          {/* v1.4.38 — Connect MAC address section */}
          <div
            data-testid="connect-mac-section"
            className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-orange-500" />
              <div className="text-sm font-semibold">Connect MAC address</div>
            </div>
            <p className="text-xs text-slate-500">
              Bind an existing client's MAC to this key. If a device with that MAC is already
              activated it will be transferred to this key's owner. MACs that already belong to a
              different reseller are refused.
            </p>
            {boundMac ? (
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-mono">
                  {boundMac.replace(/(.{2})/g, "$1:").replace(/:$/, "")}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDisconnectMac}
                  disabled={macBusy}
                  data-testid="connect-mac-disconnect"
                  className="text-red-600 hover:bg-red-50"
                >
                  <Link2Off className="w-3.5 h-3.5 mr-1" />
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  data-testid="connect-mac-input"
                  value={macDraft}
                  onChange={(e) => setMacDraft(e.target.value)}
                  placeholder="e.g. AA:BB:CC:11:22:33"
                  className="h-9 font-mono text-sm bg-white"
                />
                <Button
                  onClick={handleConnectMac}
                  disabled={macBusy || !macDraft.trim()}
                  data-testid="connect-mac-submit"
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {macBusy ? "…" : "Connect"}
                </Button>
              </div>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {/* Search — filters by playlist name / description / m3u url */}
            {playlists.length > 1 && (
              <div className="sticky top-0 -mt-1 pt-1 pb-2 bg-white z-10">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    data-testid="assign-playlists-search"
                    autoFocus
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                    placeholder={`Search ${playlists.length} playlists by name…`}
                    className="pl-8 h-9 text-sm"
                  />
                  {assignSearch && (
                    <button
                      type="button"
                      onClick={() => setAssignSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      title="Clear"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {playlists.length === 0 && !showInlineForm ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                No playlists yet. Add one below.
              </p>
            ) : (
              (() => {
                const q = assignSearch.trim().toLowerCase();
                const matched = q
                  ? playlists.filter(
                      (p) =>
                        (p.name || "").toLowerCase().includes(q) ||
                        (p.description || "").toLowerCase().includes(q) ||
                        (p.m3u_url || "").toLowerCase().includes(q)
                    )
                  : playlists;
                if (matched.length === 0) {
                  return (
                    <p className="text-sm text-slate-500 py-6 text-center">
                      No playlists match &ldquo;{assignSearch}&rdquo;.
                    </p>
                  );
                }
                return matched.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedPlaylistIds.includes(p.id)}
                      onCheckedChange={() => togglePlaylist(p.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{p.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {p.channels_count} channels · {p.description || "No description"}
                      </div>
                    </div>
                  </label>
                ));
              })()
            )}

            {/* Inline create-playlist form */}
            {!showInlineForm ? (
              <button
                type="button"
                data-testid={ADMIN.btnInlineAddPlaylist}
                onClick={() => setShowInlineForm(true)}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50/30 transition-colors text-sm font-medium"
              >
                <PlusCircle className="w-4 h-4" />
                Add new playlist for this key
              </button>
            ) : (
              <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-sm text-slate-900">New playlist</h4>
                  <button
                    data-testid={ADMIN.inlinePlaylistCancel}
                    onClick={() => {
                      setShowInlineForm(false);
                      setInlineForm({ name: "", m3u_url: "", description: "" });
                    }}
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    data-testid={ADMIN.inlinePlaylistName}
                    value={inlineForm.name}
                    onChange={(e) => setInlineForm({ ...inlineForm, name: e.target.value })}
                    placeholder="e.g. Premium IPTV Pack"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">M3U URL *</Label>
                  <Input
                    data-testid={ADMIN.inlinePlaylistUrl}
                    value={inlineForm.m3u_url}
                    onChange={(e) => setInlineForm({ ...inlineForm, m3u_url: e.target.value })}
                    placeholder="https://example.com/playlist.m3u"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Textarea
                    data-testid={ADMIN.inlinePlaylistDesc}
                    value={inlineForm.description}
                    onChange={(e) =>
                      setInlineForm({ ...inlineForm, description: e.target.value })
                    }
                    placeholder="Notes about this playlist..."
                    rows={2}
                    className="bg-white"
                  />
                </div>
                <Button
                  data-testid={ADMIN.inlinePlaylistCreate}
                  onClick={handleInlineCreate}
                  disabled={creatingInline}
                  size="sm"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  {creatingInline ? "Parsing M3U..." : "Create & auto-select"}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={ADMIN.assignSave}
              onClick={saveAssignments}
              disabled={assigning}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {assigning ? "Saving..." : `Save (${selectedPlaylistIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
